import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { HeartPulse, ShieldCheck, ArrowRight, Sparkles, CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

export default function Login() {
    const [isLoading, setIsLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        const data = new FormData(e.target);
        await login(data.get('email'), data.get('password'));
        setIsLoading(false);
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 font-inter relative overflow-hidden">
            {/* Background Aesthetics */}
            <div className="absolute top-0 left-0 w-full h-full bg-mesh-gradient opacity-20 -z-10 animate-pulse-slow"></div>
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-brand-500/10 rounded-full blur-[120px] -z-10"></div>
            <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-accent-500/10 rounded-full blur-[120px] -z-10"></div>

            <div className="max-w-6xl w-full flex flex-col md:flex-row bg-white rounded-[3rem] shadow-premium border border-slate-100 overflow-hidden mx-4">
                {/* Left Side: Branding & Info */}
                <div className="md:w-1/2 bg-slate-900 p-12 text-white flex flex-col justify-between relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-full h-full bg-brand-600/10 blur-3xl rounded-full translate-x-1/2 -translate-y-1/2"></div>
                    
                    <div className="relative z-10">
                        <Link to="/Screening_test" className="flex items-center gap-3 mb-16 group">
                            <div className="h-12 w-12 rounded-2xl bg-brand-600 flex items-center justify-center text-white shadow-lg transition-transform group-hover:scale-110">
                                <HeartPulse className="h-7 w-7" />
                            </div>
                            <span className="text-2xl font-black font-outfit tracking-tight">P2G Life</span>
                        </Link>
                        
                        <h1 className="text-5xl font-black font-outfit leading-tight mb-6">
                            Clinical Excellence, <br/>
                            <span className="text-brand-400">Simplified.</span>
                        </h1>
                        <p className="text-slate-400 text-lg font-light leading-relaxed max-w-sm">
                            Access your professional assessment tools and patient growth directories in one secure environment.
                        </p>
                    </div>

                    <div className="relative z-10 space-y-6 pt-12">
                         <div className="flex items-center gap-4 bg-white/5 p-4 rounded-3xl border border-white/5 backdrop-blur-sm">
                            <div className="h-10 w-10 rounded-xl bg-brand-500/20 flex items-center justify-center text-brand-400">
                                <ShieldCheck className="h-5 w-5" />
                            </div>
                            <p className="text-xs font-bold uppercase tracking-widest text-slate-300">HIPAA Compliant Environment</p>
                         </div>
                         <div className="flex items-center gap-4 bg-white/5 p-4 rounded-3xl border border-white/5 backdrop-blur-sm">
                            <div className="h-10 w-10 rounded-xl bg-accent-500/20 flex items-center justify-center text-accent-400">
                                <Sparkles className="h-5 w-5" />
                            </div>
                            <p className="text-xs font-bold uppercase tracking-widest text-slate-300">Advanced Outcome Analytics</p>
                         </div>
                    </div>
                </div>

                {/* Right Side: Login Form */}
                <div className="md:w-1/2 p-12 md:p-20 flex flex-col justify-center bg-white">
                    <div className="max-w-md w-full mx-auto space-y-10">
                        <div>
                            <h2 className="text-4xl font-black text-slate-900 font-outfit tracking-tight">Sign In</h2>
                            <p className="text-slate-400 mt-3 font-medium">Please enter your clinical credentials.</p>
                        </div>

                        <form className="space-y-6" onSubmit={handleLogin}>
                            <div className="space-y-5">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Professional Email</label>
                                    <input 
                                        name="email" 
                                        type="email" 
                                        required 
                                        placeholder="clinician@institution.com" 
                                        className="w-full h-16 px-6 rounded-2xl bg-slate-50 border border-slate-100 focus:ring-2 focus:ring-brand-500 font-bold text-slate-900 transition-all placeholder:text-slate-300"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center px-1">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Password</label>
                                        <a href="#" className="text-[10px] font-black text-brand-600 uppercase tracking-widest hover:text-brand-700">Forgot?</a>
                                    </div>
                                    <input 
                                        name="password" 
                                        type="password" 
                                        required 
                                        placeholder="••••••••" 
                                        className="w-full h-16 px-6 rounded-2xl bg-slate-50 border border-slate-100 focus:ring-2 focus:ring-brand-500 font-bold text-slate-900 transition-all placeholder:text-slate-300"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center gap-3 px-1">
                                <input type="checkbox" id="remember" className="h-5 w-5 rounded-lg border-slate-200 text-brand-600 focus:ring-brand-500" />
                                <label htmlFor="remember" className="text-sm font-bold text-slate-500 select-none">Keep me authenticated</label>
                            </div>

                            <Button type="submit" className="w-full h-16 rounded-2xl bg-brand-600 hover:bg-brand-700 text-white font-black text-sm uppercase tracking-widest shadow-premium transition-all translate-y-2" isLoading={isLoading}>
                                Secure Login <ArrowRight className="ml-3 h-4 w-4" />
                            </Button>
                        </form>

                        <div className="pt-8 border-t border-slate-100 text-center">
                            <p className="text-sm text-slate-400 font-medium">
                                Don't have a practice account?{' '}
                                <Link to="/Screening_test/register" className="text-brand-600 font-black hover:text-brand-500 transition-colors">
                                    Register Institution
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
